import React, { useState, useRef, useEffect } from 'react';
import './EmailChipsInput.css';

const EmailChipsInput = ({ value, onChange, placeholder, label, name }) => {
  const [inputValue, setInputValue] = useState('');
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Helper function to extract email from "Name <email@domain.com>" format
  const extractEmailAddress = (emailString) => {
    if (!emailString) return '';
    // Check if it's in "Name <email>" format
    const match = emailString.match(/<([^>]+)>/);
    if (match) {
      return match[1].trim();
    }
    // Otherwise return as is (it's probably just the email)
    return emailString.trim();
  };

  // Email validation regex
  const validateEmail = (email) => {
    // First extract the actual email address
    const actualEmail = extractEmailAddress(email);
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(actualEmail);
  };

  // Initialize emails from value prop (comma-separated string)
  useEffect(() => {
    if (value && typeof value === 'string') {
      const initialEmails = value
        .split(',')
        .map(email => email.trim())
        .filter(email => email && validateEmail(email))
        .map(email => extractEmailAddress(email)); // Extract just the email address
      setEmails(initialEmails);
    } else if (!value) {
      // Clear emails if value is empty
      setEmails([]);
    }
  }, [value]);

  // Detect if user finished typing an email
  const shouldAddEmail = (value) => {
    // Check for common email endings followed by space
    const emailEndings = [
      '.com ', '.com.br ', '.org ', '.net ', '.edu ', '.gov ', 
      '.co ', '.io ', '.app ', '.dev ', '.tech ', '.info '
    ];
    
    return emailEndings.some(ending => value.toLowerCase().includes(ending));
  };

  // Add email to the list
  const addEmail = (email) => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) return;
    
    if (!validateEmail(trimmedEmail)) {
      setError('Email inválido');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // Extract just the email address
    const actualEmail = extractEmailAddress(trimmedEmail);
    
    if (emails.includes(actualEmail)) {
      setError('Email já adicionado');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    const newEmails = [...emails, actualEmail];
    setEmails(newEmails);
    setInputValue('');
    setError('');
    
    // Call onChange with comma-separated string
    onChange({
      target: {
        name: name,
        value: newEmails.join(', ')
      }
    });
  };

  // Remove email from the list
  const removeEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
    
    // Call onChange with comma-separated string
    onChange({
      target: {
        name: name,
        value: newEmails.join(', ')
      }
    });
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Check if we should add the email automatically
    if (shouldAddEmail(value)) {
      const emailToAdd = value.trim().replace(/\s+$/, ''); // Remove trailing spaces
      addEmail(emailToAdd);
    }
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        // If comma, remove it from the input value
        const emailToAdd = inputValue.replace(/,$/, '').trim();
        addEmail(emailToAdd);
      }
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove last email if backspace pressed with empty input
      removeEmail(emails.length - 1);
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const pastedEmails = pastedText
      .split(/[,;\s]+/)
      .map(email => email.trim())
      .filter(email => email);
    
    pastedEmails.forEach(email => {
      if (validateEmail(email) && !emails.includes(email)) {
        addEmail(email);
      }
    });
  };

  // Focus input when clicking on container
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div className="email-chips-container">
      <div 
        ref={containerRef}
        className={`email-chips-input-wrapper ${isInputFocused ? 'focused' : ''} ${error ? 'error' : ''}`}
        onClick={handleContainerClick}
      >
        <div className="email-chips-list">
          {emails.map((email, index) => (
            <div key={index} className="email-chip">
              <span className="email-chip-text">{email}</span>
              <button
                type="button"
                className="email-chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmail(index);
                }}
                aria-label={`Remover ${email}`}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            placeholder={emails.length === 0 ? placeholder : ''}
            className="email-chips-input"
            aria-label={label || placeholder}
          />
        </div>
      </div>
      {error && <div className="email-chips-error">{error}</div>}
      {emails.length > 0 && (
        <div className="email-chips-count">
          {emails.length} {emails.length === 1 ? 'destinatário' : 'destinatários'}
        </div>
      )}
    </div>
  );
};

export default EmailChipsInput;
